import cookies from "browser-cookies";
import { v1 as uuidv1 } from "uuid";
import type { LDContext, LDOptionsBase } from "launchdarkly-js-sdk-common";
import { cachedStore } from "../helpers/storeHelpers";
import {
  useLDClient,
  type ProviderConfig,
} from "launchdarkly-react-client-sdk";
import type { AppState } from "../@types";
import { config } from "../config";
import type { LDClient } from "launchdarkly-js-client-sdk";
import { getBeneIsCsr } from "./csrHelpers";
import { useCallback } from "react";

export const ldOptions = {
  baseUrl: config.LD_BASE_URL,
  eventsUrl: config.LD_EVENTS_URL,
  streamUrl: config.LD_STREAM_URL,
  fetchGoals: false,
  sendLDHeaders: false,
  streaming: true,
  streamReconnectDelay: 5000,
};

/**
 * Get the most up-to-date values for a Launch Darkly user object
 * @param [state] - Application state from the store
 * @returns - the LaunchDarkly context object
 */
export const getLdUser = (
  state: AppState | Partial<AppState> | null = {}
): LDContext => {
  let key = "";
  let contextAttributes: {
    browserWidth: number;
    browserHeight: number;
    beneficiaryKey?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- isCSR is a value shared by both FE and API in LD and the casing must match
    isCSR?: boolean;
    isLoggedIn?: boolean;
    isSynthetic?: boolean;
    lastLoginDate?: string;
  } = {
    browserWidth: window.innerWidth,
    browserHeight: window.innerHeight,
  };

  if (state?.beneficiary) {
    key = state.beneficiary.meta_data.beneficiary_key;
    contextAttributes = {
      ...contextAttributes,
      beneficiaryKey: key,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- isCSR is a value shared by both FE and API in LD and the casing must match
      isCSR: getBeneIsCsr(state.beneficiary),
      isLoggedIn: state.beneficiary.meta_data.is_logged_in,
      isSynthetic: state.beneficiary.meta_data.is_synthetic,
    };

    if (state.beneficiary.meta_data.last_login_date) {
      contextAttributes["lastLoginDate"] =
        state.beneficiary.meta_data.last_login_date;
    }
  } else {
    key = cookies.get("mct-anonymous-session-id") || uuidv1();
  }

  const ldUser: LDContext = {
    kind: "user",
    key,
    anonymous: false,
    ...contextAttributes,
  };

  return ldUser;
};

export const ldProviderConfig: ProviderConfig & { options: LDOptionsBase } = {
  clientSideID: config.LD_CLIENT_ID,
  user: getLdUser(cachedStore),
  options: ldOptions,
};

export const trackLdEvent = ({
  eventName,
  data,
  metricValue,
  ldClient,
  variation,
}: {
  eventName: string;
  data?: Record<string, unknown>;
  metricValue?: number;
  ldClient: LDClient | undefined;
  variation?: string;
}): void => {
  // For testing purposes, use optional chaining for `utag.gdpr.getConsentState`
  const userPrivacyConsentValues = window.utag?.gdpr?.getConsentState();
  let analyticsConsent = false;

  if (Array.isArray(userPrivacyConsentValues)) {
    const consent = userPrivacyConsentValues?.find(
      value => value.name === "analytics"
    );
    analyticsConsent = consent?.ct === "1";
  } else {
    analyticsConsent = userPrivacyConsentValues === 1;
  }

  if (analyticsConsent) {
    ldClient?.track(
      eventName,
      { ...data, browserViewport: window.innerWidth, variation },
      metricValue
    );

    ldClient?.flush();
  }
};

export function useTrackLdEvent() {
  const ldClient = useLDClient();
  return useCallback(
    ({
      eventName,
      data,
      metricValue,
      variation,
    }: {
      eventName: string;
      data?: Record<string, unknown>;
      metricValue?: number;
      variation?: string;
    }) =>
      trackLdEvent({
        eventName,
        data,
        ldClient,
        metricValue,
        variation,
      }),
    [ldClient]
  );
}
